/**
 * Google Analytics Provider
 * Wrapper around nuxt-gtag — emits legacy clientportal-core GA event names and wrapper fan-out.
 */

import { BaseAnalyticsProvider } from './base.provider'
import { CREATOR_ACTIVITY_LEGACY_EVENTS, LEGACY_WRAPPED_EVENT_NAMES, LegacyGaEventNames } from '../constants/legacy-ga-events.constants'
import { isGoogleAnalyticsSendEnabled } from '../utils/is-google-analytics-send-enabled.utils'
import type { AnalyticsEventProperties } from '../types/properties'

interface GaEventPayload {
  gaEventName: string
  params: Record<string, unknown>
}

export interface GoogleAnalyticsLogger {
  debug?: (message: string, context?: Record<string, unknown>) => void
  warn: (message: string, context?: Record<string, unknown>) => void
  error: (message: string, context?: Record<string, unknown>) => void
}

/** The gtag commands used by analytics registration and event delivery. */
export interface GoogleTagFn {
  (command: 'event', eventName: string, params?: Record<string, unknown>): void
  (command: 'config', measurementId: string, params?: Record<string, unknown>): void
}

/** Keys never sent to GA4 (legacy parity: pseudonymous user_id only). */
const GA_PII_PARAM_KEYS = new Set(['email', 'phone', 'full_name', 'fullName', 'first_name', 'last_name'])

export class GoogleAnalyticsProvider extends BaseAnalyticsProvider<GaEventPayload> {
  readonly name = 'GoogleAnalytics'

  private gtag: GoogleTagFn | null = null
  private debug = false
  private appEnv = 'development'
  private customLoginFired = false
  private logger: GoogleAnalyticsLogger | null = null

  constructor(debug = false, logger?: GoogleAnalyticsLogger) {
    super()
    this.debug = debug
    this.logger = logger ?? null
  }

  setAppEnv(appEnv: string): void {
    this.appEnv = appEnv
  }

  setGtag(gtag: GoogleTagFn): void {
    this.gtag = gtag
    if (this.debug) this.logger?.debug?.('[GoogleAnalytics] gtag instance set')
  }

  /** @internal test reset */
  resetSessionFlagsForTests(): void {
    this.customLoginFired = false
  }

  private get canSendToGoogleAnalytics(): boolean {
    return isGoogleAnalyticsSendEnabled(this.appEnv)
  }

  transform(event: string, properties: AnalyticsEventProperties): GaEventPayload | null {
    const gaEventName = event.trim()
    if (!gaEventName) return null

    const params = this.applyLegacyGokollabParamAliases(gaEventName, this.toSnakeCase(this.stripGaPii(this.flattenGaParams(properties))))

    return { gaEventName, params }
  }

  /** Merges v-track `extra` bag into top-level params for flat GA4 key-value pairs. */
  private flattenGaParams(properties: AnalyticsEventProperties): Record<string, unknown> {
    const { userId: _userId, contactId, extra, ...rest } = properties
    const extraBag = extra && typeof extra === 'object' && !Array.isArray(extra) ? (extra as Record<string, unknown>) : {}

    return {
      ...rest,
      ...extraBag,
      ...(contactId ? { user_id: contactId } : {}),
    }
  }

  /** Maps catalog property names to legacy gaAnalytics.ts param keys for GoKollab dashboards. */
  private applyLegacyGokollabParamAliases(gaEventName: string, params: Record<string, unknown>): Record<string, unknown> {
    const result = { ...params }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_SEARCH) {
      if (result.search_text === undefined && result.query !== undefined) {
        result.search_text = result.query
      }
    }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_CATEGORY_SWITCH) {
      if (result.category === undefined) {
        result.category = result.category_slug ?? result.categorySlug ?? 'all'
      }
    }

    if (gaEventName === LegacyGaEventNames.GOKOLLAB_LISTING_VIEW || gaEventName === LegacyGaEventNames.GOKOLLAB_LISTING_JOINED) {
      if (result.slug === undefined) {
        result.slug = result.listing_slug ?? result.listingSlug
      }
      if (result.type === undefined) {
        result.type = result.listing_type ?? result.listingType ?? result.entity_type ?? result.entityType
      }
    }

    if (
      gaEventName === LegacyGaEventNames.GOKOLLAB_HOMEPAGE_LAND ||
      gaEventName === LegacyGaEventNames.GOKOLLAB_COMMUNITY_CREATE ||
      gaEventName === LegacyGaEventNames.GOKOLLAB_COURSE_CREATE
    ) {
      if (result.logged_in === undefined && result.loggedIn !== undefined) {
        result.logged_in = result.loggedIn
      }
    }

    return result
  }

  send({ gaEventName, params }: GaEventPayload): void {
    if (!this.gtag) {
      if (this.debug) this.logger?.warn('[GoogleAnalytics] gtag not available')
      return
    }

    if (!this.canSendToGoogleAnalytics) {
      if (this.debug) this.logger?.debug?.('[GoogleAnalytics] Event skipped outside enabled environment', { eventName: gaEventName })
      return
    }

    if (gaEventName === LegacyGaEventNames.CUSTOM_LOGIN_EVENT) {
      if (this.customLoginFired) {
        return
      }
      this.customLoginFired = true
    }

    if (LEGACY_WRAPPED_EVENT_NAMES.has(gaEventName as (typeof LegacyGaEventNames)[keyof typeof LegacyGaEventNames])) {
      const wrapperEvent = CREATOR_ACTIVITY_LEGACY_EVENTS.has(gaEventName as (typeof LegacyGaEventNames)[keyof typeof LegacyGaEventNames])
        ? LegacyGaEventNames.CREATOR_ACTIVITY_EVENT
        : LegacyGaEventNames.USER_ACTIVITY_EVENT
      this.emitEvent(wrapperEvent, params)
    }

    this.emitEvent(gaEventName, params)
  }

  private stripGaPii(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      if (GA_PII_PARAM_KEYS.has(key)) continue
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      if (GA_PII_PARAM_KEYS.has(snakeKey)) continue
      result[key] = value
    }
    return result
  }

  private emitEvent(gaEventName: string, params: Record<string, unknown>): void {
    if (!this.gtag) return
    try {
      this.gtag('event', gaEventName, params)
      if (this.debug) this.logger?.debug?.('[GoogleAnalytics] Event tracked', { eventName: gaEventName })
    } catch (error) {
      this.logger?.error('[GoogleAnalytics] Event delivery failed', { eventName: gaEventName, error })
    }
  }

  private toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const key of Object.keys(obj)) {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
      result[snakeKey] = obj[key]
    }
    return result
  }
}
